import { FC, PropsWithChildren, useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import {
  formatCodeName,
  formatDate,
  InputProcessingErrorType,
  InputProcessingEventDetailsType,
  Table,
  translateProcessingError,
} from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './InputDataProcessingEventReportTable.translations.json';
import { useErrorReportDescriptionOptions } from '../../output-data/errorReport/useErrorReportDescriptionOptions';

const { addTranslations, useTranslation } = i18nNsFactory(
  'input-data-processing-event-report-table',
);
addTranslations(translations);

const columnHelper = createColumnHelper<InputProcessingErrorType>();

export interface InputDataProcessingEventReportTableProps
  extends PropsWithChildren {
  processingEventDetails: InputProcessingEventDetailsType;
}

export const InputDataProcessingEventReportTable: FC<
  InputDataProcessingEventReportTableProps
> = (props) => {
  const { t } = useTranslation();
  const { processingErrors, processingEvent } = props.processingEventDetails;
  const { valueDate, dataObjectType, dataObjectCodeName } = processingEvent;

  const columns = useMemo(() => {
    const formattedValueDate = formatDate(valueDate);
    const groupName = dataObjectType === 'group' ? dataObjectCodeName : '';

    return [
      columnHelper.accessor((rowData) => rowData.createdAt, {
        id: 'sequenceNumber',
        header: t('column.sequenceNumber'),
        cell: (props) => props.row.index + 1,
        enableColumnFilter: false,
        enableSorting: false,
        meta: {
          width: 100,
          className: 'sticky-left',
        },
      }),
      columnHelper.display({
        id: 'valueDate',
        header: t('column.valueDate'),
        cell: () => formattedValueDate,
        enableSorting: false,
        meta: {
          width: '17ch',
        },
      }),
      columnHelper.display({
        id: 'dataObjectGroup',
        header: t('column.dataObjectGroup'),
        cell: () => groupName,
      }),
      columnHelper.accessor(
        (rowData) =>
          formatCodeName({
            code: rowData.dataObjectDescriptionCode,
            name: rowData.dataObjectDescriptionName,
          }),
        {
          id: 'dataObjectDescription',
          header: t('column.dataObjectDescription'),
          filterFn: 'select',
        },
      ),
      columnHelper.accessor(
        (rowData) =>
          formatCodeName({
            code: rowData.dataSubjectCode,
            name: rowData.dataSubjectName,
          }),
        {
          id: 'dataSubject',
          header: t('column.dataSubject'),
        },
      ),
      columnHelper.accessor(translateProcessingError, {
        id: 'description',
        header: t('column.description'),
      }),
    ];
  }, [t, dataObjectType, dataObjectCodeName, valueDate]);

  const descriptionOptions = useErrorReportDescriptionOptions(processingErrors);

  const options = useMemo(
    () => ({
      dataObjectDescription: descriptionOptions,
    }),
    [descriptionOptions],
  );

  return (
    <Table
      columns={columns}
      options={options}
      data={processingErrors}
      name="processing-errors"
      sticky
      scrollContainerSelector="#processing-error-report-main-content"
    />
  );
};
